import React, { useEffect, useMemo, useRef } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  getPorkPigsProducedPerYear,
  inputStateForValue,
  isNullOrUndefined,
  isValidNonnegativeNumber,
  isValidPercentageDecimal,
  isValidPositiveNumber,
  queryIsLoading,
  useGetCarbonProgramByFarmId,
  useGetFootprintingPigletFootprint,
  useGetFootprintingPorkFeedInformationByPrimaryDataId,
  useGetFootprintingPorkFootprint,
  useGetPorkFeedTypes,
  useKeyedFormList,
  useMutateFootprintingPorkFeedInformation,
  usePracticeManagementPorkParameters,
  useSaveFormOnUnmount,
  useTranslation,
} from "utils";

import { DEFAULT_DRY_MATTER_PERCENTAGE, HARD_INPUT_STATES, INPUT_STATE, KG_PER_TONNE, TOO_HIGH_INPUT_STATES } from "consts";

import { PorkFeedType, UploadablePorkFeedInformation } from "models";

import {
  AlertBanner,
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumber,
  Option,
  PageSpin,
  ProgressBarCardWithContent,
  SanityCheck,
  Select,
  Switch,
  Tag,
  Typography,
} from "components";

interface PorkFeedInfoFormProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const createFeedTypePlaceholder = (): UploadablePorkFeedInformation => ({
  id: undefined,
  feedId: "",
  isSelfProduced: null,
  totalAmountFedTs: null,
  silagePct: null,
  energyValue: null,
  dryMatterPct: null,
  emissionFactorKgCo2Eq: null,
});

const getEnergyValuesFromFeedType = (feedType: PorkFeedType | undefined): { energyValue: number | null } => {
  if (!feedType) return { energyValue: null };
  return { energyValue: feedType.energyMjMeKgDm ?? null };
};

// MJ ME per kg of weight gain over the whole fattening period. TLL Jena, Schweinefütterung
// Heft 3/2011, Tab. 19-21.
const EXPECTED_FEED_ENERGY_PER_KG_GAIN_PER_PIG: PracticeManagement.ParameterBounds = [-Infinity, 33, 45, Infinity];

// Both checks below read totalAmountFedTs as tonnes fed as-is (fresh weight), then convert to kg dry
// matter via dryMatterPct before pairing with energyMjMeKgDm.

// Typical energy density of fattening feed, MJ ME per kg dry matter.
const TYPICAL_FEED_ENERGY_MJ_PER_KG_DM = 12.8;

// Feed conversion ratio, kg feed / kg weight gain. Derived from the energy corridor rather than
// hardcoded so the two checks can never contradict each other on a normal farm — the same reasoning
// the piglet flow records. The derivation is calibrated rather than arbitrary: LfL's published
// corridor of 1:2,6-3,5 and the energy corridor above are mutually consistent at ~12.8 MJ ME/kg, so
// this reproduces the published band to within 1% while staying tied to it.
const EXPECTED_PORK_FCR: PracticeManagement.ParameterBounds = [
  -Infinity,
  EXPECTED_FEED_ENERGY_PER_KG_GAIN_PER_PIG[1] / TYPICAL_FEED_ENERGY_MJ_PER_KG_DM,
  EXPECTED_FEED_ENERGY_PER_KG_GAIN_PER_PIG[2] / TYPICAL_FEED_ENERGY_MJ_PER_KG_DM,
  Infinity,
];

const PorkFeedInformationForm = ({ primaryDataId, readOnly, farmId, year }: PorkFeedInfoFormProps) => {
  const { t } = useTranslation(["footprintingPork", "pork"]);
  const formRef = useRef<FormInstance>(null);

  const { data: carbonProgram } = useGetCarbonProgramByFarmId(farmId);
  const porkFootprintQuery = useGetFootprintingPorkFootprint(farmId, year);
  const feedInformationQuery = useGetFootprintingPorkFeedInformationByPrimaryDataId(primaryDataId ?? "");
  const porkFeedsQuery = useGetPorkFeedTypes();
  const pigletFootprintQuery = useGetFootprintingPigletFootprint(farmId, year, {
    enabled: porkFootprintQuery.data?.footprint?.pigletSourcing?.sourcingType === "self_produced",
  });
  const porkPracticeQuery = usePracticeManagementPorkParameters(carbonProgram?.country?.toLowerCase() as PracticeManagement.CountryCode);

  const isLoading = [porkFootprintQuery, feedInformationQuery, porkFeedsQuery, pigletFootprintQuery, porkPracticeQuery].some(queryIsLoading);
  const keyedFeedInformations = useKeyedFormList<UploadablePorkFeedInformation>(formRef, { isReady: !isLoading });
  const selectGroupName = "footprinting-pork-feed-crop-select";

  const { mutate: mutateFeedInformation } = useMutateFootprintingPorkFeedInformation(
    farmId,
    year,
    keyedFeedInformations.payload.filter((f) => !!f.feedId),
    primaryDataId ?? ""
  );

  const markFormPopulated = useSaveFormOnUnmount(mutateFeedInformation, !readOnly && !isLoading, !primaryDataId);

  useEffect(() => {
    if (keyedFeedInformations.entries.length === 0 || keyedFeedInformations.entries.some((f) => !f.id)) return;
    formRef.current?.validateFields();
  }, [keyedFeedInformations.entries]);

  useEffect(() => {
    if (!feedInformationQuery.data || !porkFeedsQuery.data) {
      return;
    }

    const newFeedTypes: UploadablePorkFeedInformation[] = feedInformationQuery.data.map((f) => ({
      id: f.id,
      feedId: f.feedId ?? "",
      isSelfProduced: f.isSelfProduced ?? null,
      totalAmountFedTs: f.totalAmountFedTs ?? null,
      silagePct: f.silagePct ?? null,
      energyValue: f.energyValue ?? null,
      dryMatterPct: f.dryMatterPct ?? null,
      emissionFactorKgCo2Eq: f.emissionFactorKgCo2Eq ?? null,
    }));

    markFormPopulated();

    // Each entry mounts its fields fresh under its own `clientKey`, so `initialValue` seeds the form without a manual sync

    keyedFeedInformations.actions.hydrate(newFeedTypes.length > 0 ? newFeedTypes : [createFeedTypePlaceholder()]);
  }, [feedInformationQuery.data, porkFeedsQuery.data, markFormPopulated, keyedFeedInformations.actions]);

  const isAnyFeedTypeSilage = keyedFeedInformations.entries.some((feed) => porkFeedsQuery.data?.find((f) => f.id === feed.feedId)?.isSilage ?? false);

  const feedEnergyValidation = useMemo(() => {
    const sourceFarms = porkFootprintQuery.data?.footprint?.pigletSourcing?.sourceFarms ?? [];
    const [totalPurchasedPigs, totalWeightPurchased] = sourceFarms.reduce(
      ([sumPigs, sumWeight], { quantityPurchased, averageWeightKg }) => [
        sumPigs + (quantityPurchased ?? 0),
        sumWeight + (averageWeightKg ?? 0) * (quantityPurchased ?? 0),
      ],
      [0, 0]
    );

    // A self-producing farm has no source farms, so its purchase totals stay at zero. Only the
    // purchased branch needs them, and requiring them of every farm is what used to withhold the
    // check from self-producing herds entirely.
    const pigStartWeight =
      porkFootprintQuery.data?.footprint?.pigletSourcing?.sourcingType === "self_produced"
        ? pigletFootprintQuery.data?.footprint?.averageSalesWeightKg ?? 0
        : totalPurchasedPigs > 0
        ? totalWeightPurchased / totalPurchasedPigs
        : 0;

    const rearingPeriodDays = porkFootprintQuery.data?.footprint?.herdDetails.rearingPeriodDays ?? 0;
    const herdSize = porkFootprintQuery.data?.footprint?.herdDetails.averageHerdSize ?? 0;
    const avgWeightAtSlaughterKg = porkFootprintQuery.data?.footprint?.liveWeight ?? 0;
    // Every one of these divides or is divided by, so they are checked for positivity rather than
    // against zero — that rejects the NaN and negative values a zero comparison lets through.
    if (![rearingPeriodDays, herdSize, avgWeightAtSlaughterKg, pigStartWeight].every((v) => v > 0)) return null;

    const averageWeightGain = avgWeightAtSlaughterKg - pigStartWeight;
    if (averageWeightGain <= 0) return null;

    // Silage rows carry a share of the silage ration, not a tonnage, so their mass is unknown at this
    // step. The banner exists to sanity-check the concentrate and mineral feed that is mixed into the
    // silage, so the check is built from the non-silage rows alone.
    const concentrateFeed = keyedFeedInformations.entries
      .filter((fi) => !porkFeedsQuery.data?.find((ft) => ft.id === fi.feedId)?.isSilage)
      .map((fi) => {
        const amountKgs = (fi.totalAmountFedTs ?? 0) * KG_PER_TONNE;
        return { amountKgs, dryMatterKgs: amountKgs * (fi.dryMatterPct ?? DEFAULT_DRY_MATTER_PERCENTAGE), energyMjPerKg: fi.energyValue ?? 0 };
      });

    // Nothing fed is nothing to judge. Without this the ratios below evaluate 0/0, and NaN compares
    // false against every bound, which reads as a passing check rather than an absent one.
    // FCR is compared against an as-fed industry corridor, so it stays on as-fed mass; energy needs the
    // dry-matter-corrected mass since energyMjPerKg is per kg dry matter.
    const totalFeedKgs = concentrateFeed.reduce((sum, { amountKgs }) => sum + amountKgs, 0);
    if (totalFeedKgs === 0) return null;

    const totalEnergy = concentrateFeed.reduce((sum, { dryMatterKgs, energyMjPerKg }) => sum + dryMatterKgs * energyMjPerKg, 0);

    const pigsProduced = getPorkPigsProducedPerYear(herdSize, rearingPeriodDays);
    const energyPerKgGainPerPig = totalEnergy / averageWeightGain / pigsProduced;
    const feedConversionRatio = totalFeedKgs / pigsProduced / averageWeightGain;

    const energyPerKgGainPerPigInputState = inputStateForValue(energyPerKgGainPerPig, EXPECTED_FEED_ENERGY_PER_KG_GAIN_PER_PIG);
    const feedConversionRatioInputState = inputStateForValue(feedConversionRatio, EXPECTED_PORK_FCR);

    return {
      pigsProduced,
      totalEnergy,
      averageWeightGain,
      energyPerKgGainPerPig,
      feedConversionRatio,
      isExpected: energyPerKgGainPerPigInputState === INPUT_STATE.expected && feedConversionRatioInputState === INPUT_STATE.expected,
      isTooHigh: TOO_HIGH_INPUT_STATES.includes(energyPerKgGainPerPigInputState) || TOO_HIGH_INPUT_STATES.includes(feedConversionRatioInputState),
    };
  }, [
    keyedFeedInformations.entries,
    pigletFootprintQuery.data?.footprint?.averageSalesWeightKg,
    porkFeedsQuery.data,
    porkFootprintQuery.data?.footprint?.herdDetails.averageHerdSize,
    porkFootprintQuery.data?.footprint?.herdDetails.rearingPeriodDays,
    porkFootprintQuery.data?.footprint?.liveWeight,
    porkFootprintQuery.data?.footprint?.pigletSourcing?.sourceFarms,
    porkFootprintQuery.data?.footprint?.pigletSourcing?.sourcingType,
  ]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Flex vertical gap={16}>
      <Card dataTestId="footprinting-pork-feed-crops-form">
        <Flex vertical>
          <Flex vertical className="padding-100 padding-left-0" gap={8}>
            <Typography variant="h3">{t("porkFeedInformationForm.header")}</Typography>
            <Typography variant="paragraph-m" color="grey-600" className="margin-0">
              {t("porkFeedInformationForm.description")}
            </Typography>
          </Flex>

          <Form ref={formRef} onValuesChange={markFormPopulated}>
            <Flex vertical gap={16}>
              {keyedFeedInformations.entries.map((feedInfo, index) => {
                const selectGroupWithId = `${selectGroupName}-${index}`;
                const { clientKey } = feedInfo;
                const updateItem = (item: Partial<UploadablePorkFeedInformation>) => {
                  keyedFeedInformations.actions.update(clientKey, item);
                };

                const selectedFeedType = porkFeedsQuery.data?.find((ft) => ft.id === feedInfo.feedId);
                const isCustomFeed = selectedFeedType?.isCustom ?? false;
                const isSilage = selectedFeedType?.isSilage ?? false;

                return (
                  <Card key={clientKey} className="feed-cards padding-200">
                    <Flex vertical gap={16}>
                      <Flex justify="space-between" align="center" vertical={false}>
                        <Typography variant="h5" style={{ flex: 1 }}>
                          {selectedFeedType
                            ? t("porkFeedInformationForm.feedTypeCount", {
                                count: index + 1,
                                cropName: t(`pork:feedTypes.${selectedFeedType.translationSlug}`, { defaultValue: selectedFeedType.translationSlug }),
                              })
                            : t("porkFeedInformationForm.feedTypeCountNoName", { count: index + 1 })}
                        </Typography>
                        {isCustomFeed && <Tag text={t("porkFeedInformationForm.question.customFeed")} colour="blue" size="small" />}
                        <IconButton
                          type="tertiary"
                          disabled={readOnly}
                          dataTestId={`delete-feed-crop-${index}`}
                          size="small"
                          iconName="tabler/trash"
                          onClick={() => keyedFeedInformations.actions.remove(clientKey)}
                        />
                      </Flex>

                      <Flex gap={16} align="center">
                        <Form.Item className="flex-1">
                          <Select
                            showSearch
                            disabled={readOnly}
                            name={`${selectGroupName}-${clientKey}`}
                            dataTestId={selectGroupWithId}
                            label={t("porkFeedInformationForm.question.feedType")}
                            rules={[{ required: true, message: "" }]}
                            initialValue={feedInfo.feedId ?? ""}
                            value={feedInfo.feedId ?? ""}
                            onChange={(selectedFeedId) => {
                              // This .find cannot fail but typescript doesn't know that
                              const feedType = porkFeedsQuery?.data?.find((feedType) => feedType.id === selectedFeedId);
                              const { energyValue } = getEnergyValuesFromFeedType(feedType);

                              // Custom Feed is the one type with no dryMatterPerFeedTypePct behind it. Left blank, both the
                              // energy check below and the calculation in peas fall back to 88% without the farmer ever seeing
                              // it — and 88% is roughly three times wrong for a silage. Seeding the field puts the assumption
                              // on screen where it can be corrected instead of being applied silently.
                              const dryMatterPct = feedType?.dryMatterPerFeedTypePct ?? (feedType?.isCustom ? DEFAULT_DRY_MATTER_PERCENTAGE : null);

                              updateItem({
                                ...feedInfo,
                                feedId: feedType?.id ?? "",
                                energyValue,
                                silagePct: null,
                                dryMatterPct,
                                totalAmountFedTs: feedType?.isSilage ? 0 : feedInfo.totalAmountFedTs,
                                emissionFactorKgCo2Eq: null,
                              });

                              formRef.current?.setFieldsValue({
                                [`energyValue-${clientKey}`]: energyValue,
                                [`dryMatterPct-${clientKey}`]: formatFractionInput(dryMatterPct),
                                [`silagePct-${clientKey}`]: null,
                                [`totalAmountFed-${clientKey}`]: feedType?.isSilage ? 0 : feedInfo.totalAmountFedTs,
                                [`emissionFactorKgCo2Eq-${clientKey}`]: null,
                              });
                            }}
                            dropdownStyle={{ zIndex: 10010 }}
                          >
                            {(porkFeedsQuery.data ?? []).map((feedType) => {
                              // Check if this feed type ID is already used in another item (Custom Feed is exempt — multiple rows are allowed)
                              const isAlreadyUsed =
                                !feedType.isCustom && keyedFeedInformations.entries.some((f) => f.feedId === feedType.id && f.feedId !== feedInfo.feedId);

                              return (
                                <Option
                                  disabled={readOnly || isAlreadyUsed}
                                  key={`${selectGroupWithId}-${feedType.id}`}
                                  data-testid={`${selectGroupWithId}-${feedType.id}`}
                                  value={feedType.id}
                                >
                                  {t(`pork:feedTypes.${feedType.translationSlug}`, { defaultValue: feedType.translationSlug })}
                                </Option>
                              );
                            })}
                          </Select>
                        </Form.Item>

                        <Flex vertical align="center" gap={8}>
                          <Typography variant="paragraph-s" className="margin-0">
                            {t("porkFeedInformationForm.question.isSelfProduced")}
                          </Typography>
                          <Switch
                            disabled={readOnly}
                            // dataTestId={`footprinting-pork-feed-is-selfProduced-${index}`}
                            value={feedInfo.isSelfProduced ?? false}
                            onChange={(v) => updateItem({ ...feedInfo, isSelfProduced: v })}
                          />
                        </Flex>
                      </Flex>

                      {isSilage ? (
                        <InputNumber
                          disabled={readOnly}
                          dataTestId={`footprinting-pork-feed-silage-pct-${index}`}
                          name={`silagePct-${clientKey}`}
                          label={t("porkFeedInformationForm.question.silagePct")}
                          suffix="%"
                          value={formatFractionInput(feedInfo.silagePct ?? null)}
                          initialValue={formatFractionInput(feedInfo.silagePct ?? null)}
                          onChange={(v) => {
                            updateItem({ ...feedInfo, silagePct: formatPercentageInput(v) });
                            setTimeout(() => {
                              keyedFeedInformations.entries.forEach((f) => {
                                if (porkFeedsQuery.data?.find((ft) => ft.id === f.feedId)?.isSilage) {
                                  formRef.current?.validateFields([`silagePct-${f.clientKey}`]);
                                }
                              });
                            }, 0);
                          }}
                          precision={2}
                          rules={[
                            {
                              required: true,
                              validator: (_: unknown, v: number) => {
                                if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                                  return Promise.reject(new Error(t("porkFeedInformationForm.error.validPercentage")));
                                }
                                const total = keyedFeedInformations.entries.reduce((sum, f, i) => {
                                  const value = i === index ? formatPercentageInput(v) ?? 0 : f.silagePct ?? 0;
                                  return sum + value;
                                }, 0);
                                if (Math.round(total * 100) > 100) {
                                  return Promise.reject(new Error(t("porkFeedInformationForm.error.combinedSilagePercentage")));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        />
                      ) : (
                        <InputNumber
                          disabled={readOnly}
                          dataTestId={`footprinting-pork-feed-amount-input-${index}`}
                          name={`totalAmountFed-${clientKey}`}
                          label={t("porkFeedInformationForm.question.totalAmountFed")}
                          suffix={t("porkFeedInformationForm.question.totalAmountFedUnit")}
                          value={feedInfo.totalAmountFedTs}
                          initialValue={feedInfo.totalAmountFedTs}
                          onChange={(v) => updateItem({ ...feedInfo, totalAmountFedTs: v })}
                          rules={[
                            {
                              required: true,
                              validator: (_: unknown, v: number) => {
                                if (isNullOrUndefined(v) || !isValidPositiveNumber(v)) {
                                  return Promise.reject(new Error(t("porkFeedInformationForm.error.validAmount")));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        />
                      )}

                      <Flex vertical gap={16}>
                        <Typography variant="h6">{t("porkFeedInformationForm.basicInformation")}</Typography>

                        <Form.Item
                          validateStatus={
                            feedInfo.energyValue
                              ? HARD_INPUT_STATES.includes(inputStateForValue(feedInfo.energyValue, porkPracticeQuery.data?.energyMJMEPerKg))
                                ? "error"
                                : "success"
                              : "success"
                          }
                        >
                          <InputNumber
                            disabled={readOnly}
                            dataTestId={`footprinting-pork-feed-energy-input-${index}`}
                            name={`energyValue-${clientKey}`}
                            label={t("porkFeedInformationForm.question.energyValue")}
                            suffix="MJ ME"
                            value={feedInfo.energyValue}
                            initialValue={feedInfo.energyValue}
                            onChange={(v) => updateItem({ ...feedInfo, energyValue: v })}
                            guidanceText={porkPracticeQuery.data?.energyMJMEPerKg}
                            rules={[
                              {
                                required: true,
                                message: t("porkFeedInformationForm.error.validNumber", {
                                  min: porkPracticeQuery.data?.energyMJMEPerKg?.[1],
                                  max: porkPracticeQuery.data?.energyMJMEPerKg?.[2],
                                }),
                              },
                            ]}
                          />
                        </Form.Item>
                        {feedInfo.energyValue ? <SanityCheck inputValue={feedInfo.energyValue} inputRange={porkPracticeQuery.data?.energyMJMEPerKg} /> : null}

                        <InputNumber
                          disabled={readOnly}
                          dataTestId={`footprinting-pork-feed-drymatter-input-${index}`}
                          name={`dryMatterPct-${clientKey}`}
                          label={t("porkFeedInformationForm.question.dryMatterPct")}
                          suffix="%"
                          value={formatFractionInput(feedInfo.dryMatterPct ?? null)}
                          initialValue={formatFractionInput(feedInfo.dryMatterPct ?? null)}
                          onChange={(v) => updateItem({ ...feedInfo, dryMatterPct: formatPercentageInput(v) })}
                          precision={2}
                          rules={[
                            {
                              required: true,
                              validator: (_: unknown, v: number) => {
                                if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                                  return Promise.reject(new Error(t("porkFeedInformationForm.error.validPercentage")));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        />

                        {isCustomFeed && (
                          <InputNumber
                            disabled={readOnly}
                            dataTestId={`footprinting-pork-feed-emission-factor-input-${index}`}
                            name={`emissionFactorKgCo2Eq-${clientKey}`}
                            label={t("porkFeedInformationForm.question.emissionFactor")}
                            tooltip={t("porkFeedInformationForm.tooltip.emissionFactor")}
                            suffix={t("porkFeedInformationForm.question.emissionFactorUnit")}
                            value={feedInfo.emissionFactorKgCo2Eq}
                            initialValue={feedInfo.emissionFactorKgCo2Eq}
                            onChange={(v) => updateItem({ ...feedInfo, emissionFactorKgCo2Eq: v })}
                            precision={4}
                            rules={[
                              {
                                required: true,
                                validator: (_: unknown, v: number) => {
                                  if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                                    return Promise.reject(new Error(t("porkFeedInformationForm.error.validEmissionFactor")));
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]}
                          />
                        )}
                      </Flex>
                    </Flex>
                  </Card>
                );
              })}
              {isAnyFeedTypeSilage && (
                <ProgressBarCardWithContent
                  currentValue={keyedFeedInformations.entries.filter((fi) => fi.feedId).reduce((sum, crop) => sum + (crop.silagePct ?? 0) * 100, 0)}
                  title={t("porkFeedInformationForm.silagePercentageSum.title")}
                  showPercentValue={true}
                  showRemainingPercentage={true}
                  onAnimationFinish={() => {}}
                  errorMessage={t("porkFeedInformationForm.silagePercentageSum.errorMessage")}
                  successMessage={t("porkFeedInformationForm.silagePercentageSum.successMessage")}
                  infoMessage={t("porkFeedInformationForm.silagePercentageSum.description")}
                />
              )}
              <Button
                type="text"
                dataTestId="add-feed-crop-button"
                disabled={readOnly}
                onClick={() => {
                  keyedFeedInformations.actions.add(createFeedTypePlaceholder());
                }}
              >
                <Icon iconName="klim/plus" className="icon inline" /> {t("porkFeedInformationForm.addFeedType")}
              </Button>
            </Flex>
          </Form>
        </Flex>
      </Card>
      {keyedFeedInformations.entries.length && feedEnergyValidation ? (
        <Card border dataTestId="footprinting-pork-feed-energy-check-card" className="padding-200">
          <Flex vertical gap={32}>
            <Flex vertical gap={8}>
              <Typography variant="h3">{t("porkFeedInformationForm.feedEnergyTitle")}</Typography>
              <Typography variant="paragraph-m" color="grey-600">
                {t("porkFeedInformationForm.feedEnergyDescription")}
              </Typography>
            </Flex>

            <AlertBanner
              type={feedEnergyValidation.isExpected ? "success" : "warning"}
              iconName={feedEnergyValidation.isExpected ? "tabler/confetti" : "tabler/info-circle"}
              iconAlign="start"
              iconSize={24}
              headingSize="h6"
              title={
                feedEnergyValidation.isExpected
                  ? t("porkFeedInformationForm.success.feedAmountInRange")
                  : feedEnergyValidation.isTooHigh
                  ? t("porkFeedInformationForm.error.feedAmountTooHigh")
                  : t("porkFeedInformationForm.error.feedAmountTooLow")
              }
              description={
                feedEnergyValidation.isExpected
                  ? t("porkFeedInformationForm.success.feedValidationDescriptionInRange")
                  : feedEnergyValidation.isTooHigh
                  ? t("porkFeedInformationForm.error.feedValidationDescriptionHigh")
                  : t("porkFeedInformationForm.error.feedValidationDescriptionLow")
              }
            />
          </Flex>
        </Card>
      ) : null}
    </Flex>
  );
};

PorkFeedInformationForm.Key = "pork.feedInformation";
export { PorkFeedInformationForm };
